import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const safeStorageGet = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeStorageSet = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures so route protection still works.
  }
};

const safeStorageRemove = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage failures so route protection still works.
  }
};

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [processingInvite, setProcessingInvite] = useState(false);
  const [justAcceptedInvite, setJustAcceptedInvite] = useState(false);
  const inviteProcessed = useRef(false);

  // Remember that this user already has a church so we never wrongly redirect to /setup
  const hasKnownChurch = session?.user?.id
    ? safeStorageGet(`church_linked_${session.user.id}`) === "1"
    : false;

  // Fetch the user's church linkage from user_roles
  const { data: userRole, isLoading: roleLoading, isError: roleError } = useQuery({
    queryKey: ["user_role_church", session?.user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("church_id, role")
        .eq("user_id", session!.user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!session,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  // Persist church linkage flag whenever we confirm the user has a church
  useEffect(() => {
    if (session?.user?.id && userRole?.church_id) {
      safeStorageSet(`church_linked_${session.user.id}`, "1");
    }
  }, [session?.user?.id, userRole?.church_id]);

  // A user with no church but a previously ACCEPTED invitation had their access
  // revoked by an admin — distinguish them from brand-new signups.
  const { data: wasRevoked, isLoading: revokedLoading } = useQuery({
    queryKey: ["revoked_check", session?.user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invitations")
        .select("id")
        .ilike("email", session!.user.email!)
        .not("accepted_at", "is", null)
        .limit(1);
      if (error) return false;
      return (data?.length ?? 0) > 0;
    },
    enabled: !!session?.user?.email && !roleLoading && !roleError && !userRole?.church_id,
    staleTime: 5 * 60 * 1000,
  });

  const acceptInviteToken = async (token: string, userId: string) => {
    await supabase.rpc("accept_invitation" as any, { _token: token, _user_id: userId });
    safeStorageRemove("pending_invite");
    queryClient.invalidateQueries({ queryKey: ["user_role_church"] });
    setJustAcceptedInvite(true);
  };

  // If a pending invite token is in localStorage, accept it once the session is active
  // This handles the email-confirmation path: invited user confirms email → lands on
  // /dashboard → ProtectedRoute processes the stored token → church link established.
  useEffect(() => {
    if (
      !session ||
      roleLoading ||
      userRole?.church_id ||   // already linked — nothing to do
      inviteProcessed.current  // prevent double execution
    ) return;

    const raw = safeStorageGet("pending_invite");
    if (!raw) return;

    let tokenData: { token: string } | null = null;
    try { tokenData = JSON.parse(raw); } catch { safeStorageRemove("pending_invite"); return; }
    if (!tokenData?.token) { safeStorageRemove("pending_invite"); return; }

    inviteProcessed.current = true;
    setProcessingInvite(true);

    acceptInviteToken(tokenData.token, session.user.id)
      .then(() => {
        // linked
      })
      .catch(() => {
        // Token may have expired; clear it so the user isn't stuck
        safeStorageRemove("pending_invite");
      })
      .finally(() => setProcessingInvite(false));
  }, [session?.user?.id, roleLoading, userRole?.church_id]);

  // Fallback for admin-invite links that don't include our custom token in the redirect URL.
  // If the signed-in user's email has an unaccepted invite, accept the latest valid one.
  useEffect(() => {
    if (
      !session?.user?.id ||
      !session.user.email ||
      roleLoading ||
      userRole?.church_id ||
      inviteProcessed.current
    ) {
      return;
    }

    inviteProcessed.current = true;
    setProcessingInvite(true);

    (async () => {
      const { data, error } = await supabase
        .from("invitations")
        .select("token")
        .eq("email", session.user.email!)
        .is("accepted_at", null)
        .gt("expires_at", new Date().toISOString())
        .order("created_at", { ascending: false })
        .limit(1);

      if (error || !data || data.length === 0) {
        return;
      }

      await acceptInviteToken(data[0].token, session.user.id);
    })()
      .catch(() => {
        // If lookup/accept fails, user will continue normal routing.
      })
      .finally(() => setProcessingInvite(false));
  }, [session?.user?.id, session?.user?.email, roleLoading, userRole?.church_id]);

  // Only block on the initial role load; don't re-block the full app shell on background refetches.
  const isLoading = loading || (!!session && roleLoading) || processingInvite || revokedLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center justify-center">
          <img
            src="/ichurch-logo.svg"
            alt="iChurch Data"
            className="h-14 w-14 rounded-2xl object-cover animate-pulse"
          />
        </div>
      </div>
    );
  }

  // Not authenticated → login
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // ── Routing rules based on user state ────────────────────────────────────────
  const pathname = location.pathname;

  if (justAcceptedInvite && pathname !== "/dashboard") {
    return <Navigate to="/dashboard" replace />;
  }

  // Previously joined a church via invitation but the role is gone → access was revoked.
  // Takes precedence over hasKnownChurch (the flag is stale for revoked users).
  if (
    !userRole?.church_id &&
    !roleError &&
    wasRevoked &&
    pathname !== "/access-revoked" &&
    pathname !== "/setup" &&
    pathname !== "/billing"
  ) {
    safeStorageRemove(`church_linked_${session.user.id}`);
    return <Navigate to="/access-revoked" replace />;
  }

  // User has no church profile yet → church setup page
  // (skip if already on /setup or /billing to avoid redirect loops)
  // Never redirect if user previously had a church (survives sleep/refresh)
  if (!userRole?.church_id && !hasKnownChurch && !roleError && pathname !== "/setup" && pathname !== "/billing" && pathname !== "/access-revoked") {
    return <Navigate to="/setup" replace />;
  }

  return <>{children}</>;
}
